import { t } from "@lingui/macro";
import {
  CircleNotch,
  Sparkle,
  Brain,
  FileText,
  TrendUp,
  Rocket,
  Planet,
  Star,
  User,
  Gear,
  RadioButton,
  Lightning,
  Atom,
  Polygon,
  Circle,
  Eye,
  Waveform,
  Pulse,
  Swatches,
  Spiral,
  Timer,
} from "@phosphor-icons/react";
import React from "react";
import "./analysis-loading.css";

export interface AnalysisLoadingProps {
  message?: string;
  subMessage?: string;
}

export const AnalysisLoading = ({
  message = t`Analyzing Your Resume`,
  subMessage = t`Our AI is examining your resume to provide detailed insights...`,
}: AnalysisLoadingProps) => {
  return (
    <div className="from-background via-muted/30 to-background relative flex h-screen items-center justify-center overflow-hidden bg-gradient-to-br">
      {/* Animated Background Effects */}
      <div className="absolute inset-0">
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div
            className="from-primary/10 via-accent/10 to-primary/10 absolute inset-0 bg-gradient-to-br"
            style={{
              background:
                "linear-gradient(-45deg, rgba(var(--primary), 0.05), rgba(var(--accent), 0.1), rgba(var(--primary), 0.05), rgba(var(--muted), 0.05))",
              backgroundSize: "400% 400%",
              animation: "gradientShift 15s ease infinite",
            }}
          />
        </div>

        {/* Enhanced Animated Circles with staggered timing */}
        <div className="absolute inset-0 opacity-[0.06] dark:opacity-[0.09]">
          <div
            className="bg-primary absolute top-1/4 left-1/4 h-48 w-48 rounded-full blur-3xl"
            style={{
              animation:
                "float 20s ease-in-out infinite, pulse 8s ease-in-out infinite, scaleBreath 15s ease-in-out infinite",
            }}
          />
          <div
            className="bg-accent absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full blur-3xl"
            style={{
              animation:
                "float 25s ease-in-out infinite reverse, pulse 10s ease-in-out infinite 2s, rotate 60s linear infinite",
            }}
          />
          <div
            className="bg-primary absolute top-1/2 right-1/3 h-32 w-32 rounded-full blur-2xl"
            style={{
              animation:
                "float 18s ease-in-out infinite 5s, pulse 6s ease-in-out infinite 1s, scaleBreath 12s ease-in-out infinite 3s",
            }}
          />
          <div
            className="bg-accent/70 absolute bottom-1/3 left-1/3 h-40 w-40 rounded-full blur-3xl"
            style={{
              animation: "float 22s ease-in-out infinite 3s, pulse 9s ease-in-out infinite 4s",
            }}
          />
        </div>

        {/* Enhanced Floating Analysis Icons with better animations */}
        <div className="absolute inset-0 opacity-20">
          {/* AI Brain Icon */}
          <div
            className="text-primary/70 absolute top-1/5 left-1/6"
            style={{
              animation:
                "float 30s ease-in-out infinite, rotate 45s linear infinite, scaleBreath 8s ease-in-out infinite 2s",
              transform: "translateZ(0)", // Hardware acceleration
            }}
          >
            <Brain className="h-8 w-8 drop-shadow-sm" />
          </div>

          {/* Document Analysis Icon */}
          <div
            className="text-accent/70 absolute top-3/5 right-1/4"
            style={{
              animation:
                "pulse 8s ease-in-out infinite 2s, float 35s ease-in-out infinite reverse, slideIn 1s ease-out",
            }}
          >
            <FileText className="h-10 w-10 drop-shadow-sm" />
          </div>

          {/* Trend Chart Icon */}
          <div
            className="text-primary/70 absolute bottom-1/3 left-2/3"
            style={{
              animation:
                "scaleBreath 12s ease-in-out infinite 4s, rotate 60s linear infinite reverse, bounceFloat 20s ease-in-out infinite",
            }}
          >
            <TrendUp className="h-7 w-7 drop-shadow-sm" />
          </div>

          {/* Sparkle Icon */}
          <div
            className="text-accent/70 absolute top-1/6 right-1/6"
            style={{
              animation:
                "float 25s ease-in-out infinite 3s, pulse 10s ease-in-out infinite, sparkle 4s ease-in-out infinite 1s",
            }}
          >
            <Sparkle className="h-9 w-9 drop-shadow-sm" />
          </div>

          {/* Second Spaceship - Different Pattern */}
          <div
            className="text-accent/50 absolute top-1/3 left-1/4"
            style={{
              animation: "spaceshipFlight2 60s ease-in-out infinite reverse",
              transform: "translateZ(0)", // Hardware acceleration
              transformOrigin: "center",
            }}
          >
            <div className="spaceship-container">
              <Rocket className="h-7 w-7 drop-shadow-lg" style={{ transform: "rotate(-30deg)" }} />
              <div className="spaceship-trail"></div>
            </div>
          </div>

          {/* Space Station */}
          <div
            className="text-primary/40 absolute top-1/5 right-1/4"
            style={{
              animation:
                "spaceStationRotate 40s linear infinite, stationPulse 6s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="space-station">
              <Polygon className="h-12 w-12 drop-shadow-lg" />
              <div className="station-lights"></div>
              <div className="station-antenna"></div>
            </div>
          </div>

          {/* Floating Astronaut 1 */}
          <div
            className="text-accent/60 absolute right-1/3 bottom-1/3"
            style={{
              animation:
                "astronautFloat 35s ease-in-out infinite, astronautWork 8s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="astronaut-container">
              <User className="h-8 w-8 drop-shadow-sm" />
              <div className="astronaut-jetpack"></div>
            </div>
          </div>

          {/* Floating Astronaut 2 */}
          <div
            className="text-primary/50 absolute top-2/3 left-1/6"
            style={{
              animation:
                "astronautFloat2 42s ease-in-out infinite reverse, astronautWork 10s ease-in-out infinite 3s",
              transform: "translateZ(0)",
            }}
          >
            <div className="astronaut-container">
              <User className="h-7 w-7 drop-shadow-sm" style={{ transform: "rotate(45deg)" }} />
              <div className="astronaut-jetpack"></div>
            </div>
          </div>

          {/* Satellite */}
          <div
            className="text-accent/45 absolute top-1/4 left-3/4"
            style={{
              animation:
                "satelliteOrbit 50s linear infinite, satelliteBeacon 4s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="satellite-container">
              <Atom className="h-9 w-9 drop-shadow-sm" />
              <div className="satellite-panels"></div>
              <div className="satellite-signal"></div>
            </div>
          </div>

          {/* Space Debris/Asteroids */}
          <div
            className="text-primary/30 absolute bottom-1/5 left-2/3"
            style={{
              animation: "debrisFloat 25s linear infinite, debrisSpin 15s linear infinite",
              transform: "translateZ(0)",
            }}
          >
            <Circle className="h-4 w-4 opacity-70 drop-shadow-sm" />
          </div>

          <div
            className="text-accent/25 absolute top-3/4 right-1/5"
            style={{
              animation:
                "debrisFloat2 30s linear infinite reverse, debrisSpin 20s linear infinite reverse",
              transform: "translateZ(0)",
            }}
          >
            <Circle className="h-3 w-3 opacity-60 drop-shadow-sm" />
          </div>

          {/* Distant Planet */}
          <div
            className="text-primary/20 absolute top-1/8 left-1/8"
            style={{
              animation: "planetRotate 80s linear infinite, planetGlow 12s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <Planet className="h-16 w-16 opacity-60 drop-shadow-xl" />
          </div>

          {/* Working Equipment */}
          <div
            className="text-accent/35 absolute right-1/6 bottom-1/6"
            style={{
              animation:
                "equipmentWork 20s ease-in-out infinite, equipmentBlink 3s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="equipment-container">
              <Gear className="h-8 w-8 drop-shadow-sm" />
              <div className="equipment-status"></div>
            </div>
          </div>

          {/* Communication Arrays */}
          <div
            className="text-primary/40 absolute top-1/2 right-1/8"
            style={{
              animation:
                "arrayRotate 25s ease-in-out infinite, arraySignal 5s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="comm-array">
              <RadioButton className="h-6 w-6 drop-shadow-sm" />
              <div className="array-signal-waves"></div>
            </div>
          </div>

          {/* Energy Field */}
          <div
            className="text-accent/30 absolute bottom-2/5 left-1/5"
            style={{
              animation:
                "energyPulse 15s ease-in-out infinite, energyFloat 45s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <Lightning className="h-7 w-7 drop-shadow-sm" />
          </div>

          {/* Twinkling Stars */}
          {[...Array(8)].map((_, i) => (
            <div
              key={`star-${i}`}
              className="text-primary/25 absolute"
              style={{
                left: Math.random() * 80 + 10 + "%",
                top: Math.random() * 80 + 10 + "%",
                animation: `starTwinkle ${4 + Math.random() * 6}s ease-in-out infinite ${Math.random() * 3}s, starFloat ${30 + Math.random() * 20}s ease-in-out infinite ${Math.random() * 10}s`,
                transform: "translateZ(0)",
              }}
            >
              <Star
                className="h-3 w-3 drop-shadow-sm"
                weight={Math.random() > 0.5 ? "fill" : "regular"}
              />
            </div>
          ))}

          {/* Cosmic Eye - Watching Observer */}
          <div
            className="text-accent/30 absolute top-1/6 left-2/3"
            style={{
              animation:
                "cosmicEyeBlink 8s ease-in-out infinite, cosmicEyeFloat 60s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="cosmic-eye">
              <Eye className="h-12 w-12 drop-shadow-xl" />
              <div className="eye-glow"></div>
            </div>
          </div>

          {/* Nebula Cloud System */}
          <div className="nebula-system pointer-events-none absolute inset-0">
            {[...Array(6)].map((_, i) => (
              <div
                key={`nebula-${i}`}
                className="nebula-cloud absolute"
                style={{
                  left: Math.random() * 90 + 5 + "%",
                  top: Math.random() * 90 + 5 + "%",
                  width: 50 + Math.random() * 100 + "px",
                  height: 30 + Math.random() * 60 + "px",
                  animation: `nebulaFloat ${40 + Math.random() * 30}s ease-in-out infinite ${Math.random() * 10}s, nebulaPulse ${8 + Math.random() * 12}s ease-in-out infinite ${Math.random() * 5}s`,
                  transform: "translateZ(0)",
                }}
              />
            ))}
          </div>

          {/* Solar Flare System */}
          <div
            className="text-accent/40 absolute right-2/3 bottom-1/8"
            style={{
              animation:
                "solarFlareErupt 20s ease-in-out infinite, solarFlareMove 45s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="solar-flare">
              <Waveform className="h-10 w-10 drop-shadow-lg" />
              <div className="flare-particles"></div>
            </div>
          </div>

          {/* Pulsing Quasar */}
          <div
            className="text-primary/35 absolute top-3/5 left-1/12"
            style={{
              animation: "quasarPulse 12s ease-in-out infinite, quasarRotate 30s linear infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="quasar">
              <Pulse className="h-8 w-8 drop-shadow-lg" />
              <div className="quasar-rings"></div>
            </div>
          </div>

          {/* Space Wormhole */}
          <div
            className="text-accent/25 absolute top-1/8 right-1/12"
            style={{
              animation:
                "wormholeRotate 25s linear infinite, wormholePulse 6s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="wormhole">
              <Spiral className="h-14 w-14 drop-shadow-xl" />
              <div className="wormhole-distortion"></div>
            </div>
          </div>

          {/* Alien Artifact */}
          <div
            className="text-primary/40 absolute bottom-1/3 left-3/4"
            style={{
              animation:
                "artifactHover 18s ease-in-out infinite, artifactGlow 4s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="alien-artifact">
              <Swatches className="h-9 w-9 drop-shadow-lg" />
              <div className="artifact-energy"></div>
            </div>
          </div>

          {/* Temporal Anomaly */}
          <div
            className="text-accent/35 absolute top-4/5 right-1/3"
            style={{
              animation:
                "temporalRift 15s ease-in-out infinite, temporalFloat 35s ease-in-out infinite",
              transform: "translateZ(0)",
            }}
          >
            <div className="temporal-anomaly">
              <Timer className="h-7 w-7 drop-shadow-sm" />
              <div className="time-distortion"></div>
            </div>
          </div>

          {/* Quantum Particles */}
          <div className="quantum-field pointer-events-none absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={`quantum-${i}`}
                className="quantum-particle absolute"
                style={{
                  left: Math.random() * 100 + "%",
                  top: Math.random() * 100 + "%",
                  width: 2 + Math.random() * 4 + "px",
                  height: 2 + Math.random() * 4 + "px",
                  animation: `quantumTunnel ${10 + Math.random() * 15}s linear infinite ${Math.random() * 8}s, quantumFlicker ${2 + Math.random() * 3}s ease-in-out infinite ${Math.random() * 2}s`,
                  transform: "translateZ(0)",
                }}
              />
            ))}
          </div>

          {/* Cosmic Lightning Storm */}
          <div className="lightning-system pointer-events-none absolute inset-0">
            {[...Array(4)].map((_, i) => (
              <div
                key={`lightning-${i}`}
                className="cosmic-lightning absolute"
                style={{
                  left: Math.random() * 90 + 5 + "%",
                  top: Math.random() * 90 + 5 + "%",
                  animation: `lightningStrike ${3 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 8}s, lightningMove ${25 + Math.random() * 15}s ease-in-out infinite ${Math.random() * 5}s`,
                  transform: "translateZ(0)",
                }}
              >
                <Lightning
                  className={`h-${4 + Math.floor(Math.random() * 4)} w-${4 + Math.floor(Math.random() * 4)} text-accent/40 drop-shadow-sm`}
                />
              </div>
            ))}
          </div>

          {/* Gravity Wells */}
          <div className="gravity-wells pointer-events-none absolute inset-0">
            {[...Array(3)].map((_, i) => (
              <div
                key={`gravity-${i}`}
                className="gravity-well absolute"
                style={{
                  left: Math.random() * 80 + 10 + "%",
                  top: Math.random() * 80 + 10 + "%",
                  width: 40 + Math.random() * 60 + "px",
                  height: 40 + Math.random() * 60 + "px",
                  animation: `gravityPull ${20 + Math.random() * 20}s ease-in-out infinite ${Math.random() * 10}s`,
                  transform: "translateZ(0)",
                }}
              />
            ))}
          </div>

          {/* Space-Time Ripples */}
          <div className="spacetime-ripples pointer-events-none absolute inset-0">
            {[...Array(5)].map((_, i) => (
              <div
                key={`ripple-${i}`}
                className="spacetime-ripple absolute"
                style={{
                  left: Math.random() * 100 + "%",
                  top: Math.random() * 100 + "%",
                  animation: `rippleExpand ${8 + Math.random() * 12}s ease-out infinite ${Math.random() * 15}s`,
                  transform: "translateZ(0)",
                }}
              />
            ))}
          </div>
        </div>

        {/* Enhanced Floating Particles */}
        <div className="absolute inset-0 opacity-25">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="from-primary/50 to-accent/50 absolute rounded-full bg-gradient-to-r"
              style={{
                width: Math.random() * 6 + 2 + "px",
                height: Math.random() * 6 + 2 + "px",
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
                animation: `floatParticle ${15 + Math.random() * 10}s linear infinite ${Math.random() * 5}s, pulse ${8 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 2}s`,
                filter: "blur(0.5px)",
              }}
            />
          ))}
        </div>

        {/* Enhanced AI Neural Network Pattern */}
        <div className="absolute inset-0 opacity-12">
          <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="nodeGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
                <stop offset="50%" stopColor="currentColor" stopOpacity="0.6" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.2" />
              </linearGradient>
            </defs>

            {/* Enhanced Neural Network Nodes */}
            <circle cx="20%" cy="25%" r="3" fill="url(#nodeGrad)" className="text-primary">
              <animate attributeName="r" values="2;5;2" dur="6s" repeatCount="indefinite" />
              <animate
                attributeName="opacity"
                values="0.5;1;0.5"
                dur="6s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="30%" cy="45%" r="2" fill="url(#nodeGrad)" className="text-accent">
              <animate
                attributeName="r"
                values="1;4;1"
                dur="8s"
                repeatCount="indefinite"
                begin="2s"
              />
            </circle>
            <circle cx="70%" cy="30%" r="3" fill="url(#nodeGrad)" className="text-primary">
              <animate
                attributeName="r"
                values="2;5;2"
                dur="7s"
                repeatCount="indefinite"
                begin="4s"
              />
            </circle>
            <circle cx="80%" cy="60%" r="2" fill="url(#nodeGrad)" className="text-accent">
              <animate
                attributeName="r"
                values="1;4;1"
                dur="9s"
                repeatCount="indefinite"
                begin="1s"
              />
            </circle>
            <circle cx="50%" cy="50%" r="2" fill="url(#nodeGrad)" className="text-primary">
              <animate
                attributeName="r"
                values="1;3;1"
                dur="5s"
                repeatCount="indefinite"
                begin="3s"
              />
            </circle>

            {/* Enhanced Connection Lines with flow animation */}
            <path
              d="M20% 25% L30% 45%"
              stroke="url(#lineGrad)"
              strokeWidth="1"
              className="text-primary/60"
              opacity="0.4"
            >
              <animate
                attributeName="opacity"
                values="0.2;0.8;0.2"
                dur="4s"
                repeatCount="indefinite"
              />
            </path>
            <path
              d="M30% 45% L70% 30%"
              stroke="url(#lineGrad)"
              strokeWidth="1"
              className="text-accent/60"
              opacity="0.4"
            >
              <animate
                attributeName="opacity"
                values="0.2;0.8;0.2"
                dur="5s"
                repeatCount="indefinite"
                begin="1s"
              />
            </path>
            <path
              d="M70% 30% L80% 60%"
              stroke="url(#lineGrad)"
              strokeWidth="1"
              className="text-primary/60"
              opacity="0.4"
            >
              <animate
                attributeName="opacity"
                values="0.2;0.8;0.2"
                dur="6s"
                repeatCount="indefinite"
                begin="2s"
              />
            </path>
            <path
              d="M50% 50% L20% 25%"
              stroke="url(#lineGrad)"
              strokeWidth="0.8"
              className="text-accent/60"
              opacity="0.3"
            >
              <animate
                attributeName="opacity"
                values="0.1;0.7;0.1"
                dur="7s"
                repeatCount="indefinite"
                begin="3s"
              />
            </path>
          </svg>
        </div>

        {/* Enhanced Analysis Keywords Floating */}
        <div className="absolute inset-0 opacity-15">
          {/* Primary Metric Analysis Badges */}
          <div
            className="from-primary/25 to-primary/15 text-primary absolute top-1/2 left-1/8 rounded-full bg-gradient-to-r px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 40s ease-in-out infinite 2s, scaleBreath 8s ease-in-out infinite, slideIn 0.8s ease-out",
            }}
          >
            Hard Skills Match
          </div>

          <div
            className="from-accent/25 to-accent/15 text-accent absolute right-1/3 bottom-1/4 rounded-full bg-gradient-to-r px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 35s ease-in-out infinite reverse 4s, pulse 12s ease-in-out infinite, slideIn 1.2s ease-out",
            }}
          >
            {t`Keyword Coverage`}
          </div>

          <div
            className="from-primary/20 to-primary/10 text-primary absolute top-2/3 right-1/5 rounded-full bg-gradient-to-r px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 42s ease-in-out infinite 6s, rotate 80s linear infinite, slideIn 1.6s ease-out",
            }}
          >
            {t`Achievement Impact`}
          </div>

          <div
            className="from-accent/20 to-accent/10 text-accent absolute top-1/3 left-3/4 rounded-full bg-gradient-to-r px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 38s ease-in-out infinite 1s, pulse 9s ease-in-out infinite 3s, slideIn 2s ease-out",
            }}
          >
            {t`Requirements Match`}
          </div>

          {/* Secondary Metric Analysis */}
          <div
            className="from-primary/22 to-primary/12 text-primary absolute bottom-1/5 left-1/3 rounded-full bg-gradient-to-r px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 45s ease-in-out infinite 8s, pulse 10s ease-in-out infinite 2s, slideIn 2.4s ease-out",
            }}
          >
            {t`Soft Skills`}
          </div>

          <div
            className="from-accent/22 to-accent/12 text-accent absolute top-1/6 right-2/5 rounded-full bg-gradient-to-r px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 37s ease-in-out infinite reverse 5s, scaleBreath 7s ease-in-out infinite 1s, slideIn 2.8s ease-out",
            }}
          >
            {t`Domain Experience`}
          </div>

          <div
            className="from-primary/18 to-primary/8 text-primary absolute right-1/8 bottom-2/5 rounded-full bg-gradient-to-r px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 39s ease-in-out infinite 7s, rotate 70s linear infinite reverse, slideIn 3.2s ease-out",
            }}
          >
            {t`Language Quality`}
          </div>

          <div
            className="from-accent/18 to-accent/8 text-accent absolute top-3/5 left-1/5 rounded-full bg-gradient-to-r px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 41s ease-in-out infinite reverse 3s, pulse 11s ease-in-out infinite 4s, slideIn 3.6s ease-out",
            }}
          >
            {t`Skill Recency`}
          </div>

          {/* Advanced Analysis Terms */}
          <div
            className="from-primary/16 to-primary/6 text-primary absolute top-1/8 left-1/2 rounded-full bg-gradient-to-r px-2 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 48s ease-in-out infinite 10s, scaleBreath 9s ease-in-out infinite 3s, slideIn 4s ease-out",
            }}
          >
            {t`Action Verbs`}
          </div>

          <div
            className="from-accent/16 to-accent/6 text-accent absolute right-2/3 bottom-1/8 rounded-full bg-gradient-to-r px-2 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 36s ease-in-out infinite reverse 6s, pulse 8s ease-in-out infinite 1s, slideIn 4.4s ease-out",
            }}
          >
            {t`Transferability`}
          </div>

          <div
            className="from-primary/14 to-primary/4 text-primary absolute top-4/5 left-2/3 rounded-full bg-gradient-to-r px-2 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 44s ease-in-out infinite 9s, rotate 60s linear infinite, slideIn 4.8s ease-out",
            }}
          >
            {t`Seniority Match`}
          </div>

          <div
            className="from-accent/14 to-accent/4 text-accent absolute top-1/4 left-1/6 rounded-full bg-gradient-to-r px-2 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 43s ease-in-out infinite reverse 4s, scaleBreath 6s ease-in-out infinite 2s, slideIn 5.2s ease-out",
            }}
          >
            {t`Red Flags`}
          </div>

          {/* Technical Analysis Terms */}
          <div
            className="from-primary/12 to-primary/2 text-primary absolute bottom-1/3 left-4/5 rounded-full bg-gradient-to-r px-1.5 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 50s ease-in-out infinite 12s, pulse 13s ease-in-out infinite 5s, slideIn 5.6s ease-out",
            }}
          >
            {t`Evidence Type`}
          </div>

          <div
            className="from-accent/12 to-accent/2 text-accent absolute top-2/5 right-1/4 rounded-full bg-gradient-to-r px-1.5 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 34s ease-in-out infinite reverse 7s, rotate 55s linear infinite reverse, slideIn 6s ease-out",
            }}
          >
            {t`Skill Gaps`}
          </div>

          <div
            className="from-primary/10 to-primary/2 text-primary absolute right-3/5 bottom-3/5 rounded-full bg-gradient-to-r px-1.5 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 46s ease-in-out infinite 11s, scaleBreath 5s ease-in-out infinite 4s, slideIn 6.4s ease-out",
            }}
          >
            {t`Confidence Score`}
          </div>

          <div
            className="from-accent/10 to-accent/2 text-accent absolute top-5/6 left-1/4 rounded-full bg-gradient-to-r px-1.5 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 33s ease-in-out infinite reverse 8s, pulse 7s ease-in-out infinite 3s, slideIn 6.8s ease-out",
            }}
          >
            {t`Match Type`}
          </div>

          {/* Specialized Metrics */}
          <div
            className="from-primary/8 to-primary/1 text-primary absolute top-3/4 right-4/5 rounded-full bg-gradient-to-r px-1.5 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 52s ease-in-out infinite 14s, rotate 45s linear infinite, slideIn 7.2s ease-out",
            }}
          >
            {t`Impact Metrics`}
          </div>

          <div
            className="from-accent/8 to-accent/1 text-accent absolute bottom-2/3 left-3/5 rounded-full bg-gradient-to-r px-1.5 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm"
            style={{
              animation:
                "float 32s ease-in-out infinite reverse 9s, scaleBreath 4s ease-in-out infinite 1s, slideIn 7.6s ease-out",
            }}
          >
            {t`Criticality Level`}
          </div>
        </div>

        {/* Enhanced Progress Indicators */}
        <div className="absolute inset-0 opacity-20">
          {/* Progress Dots */}
          <div
            className="absolute bottom-1/2 left-1/5 flex space-x-2"
            style={{
              animation: "float 43s ease-in-out infinite 4s, pulse 14s ease-in-out infinite",
            }}
          >
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-primary h-2 w-2 rounded-full"
                style={{
                  animation: `pulse 2s ease-in-out infinite ${i * 0.3}s, scaleBreath 4s ease-in-out infinite ${i * 0.5}s`,
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Content */}
      <div className="relative z-10 space-y-8 px-6 text-center">
        {/* Enhanced Main Spinner */}
        <div className="relative">
          <div className="from-primary/15 to-accent/10 mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br shadow-lg backdrop-blur-sm">
            <CircleNotch className="text-primary h-10 w-10 animate-spin drop-shadow-sm" />
          </div>

          {/* Enhanced pulse rings around spinner */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="border-primary/30 h-24 w-24 animate-pulse rounded-full border-2"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="border-primary/20 h-28 w-28 animate-pulse rounded-full border"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="border-accent/15 h-32 w-32 animate-pulse rounded-full border"
              style={{ animationDelay: "2s" }}
            ></div>
          </div>
        </div>

        {/* Static Analysis Messages */}
        <div className="max-w-lg space-y-4">
          <div>
            <h3 className="text-foreground from-primary/50 to-accent/50 mb-2 bg-gradient-to-l bg-clip-text text-2xl font-bold">
              {message}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{subMessage}</p>
          </div>

          {/* Enhanced loading dots */}
          <div className="mt-6 flex items-center justify-center space-x-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-primary h-1.5 w-1.5 rounded-full"
                style={{
                  animation: `bounce 1.4s ease-in-out infinite ${i * 0.2}s`,
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
